import React, { useEffect, useState, useRef, useCallback } from "react";
import { IframeModalView } from "../IframeModalView";
import { useAppSelector, useAppDispatch } from "hooks";
import { closeIframeModal, openIframeModal, setIsIframeRefreshing } from "state/reducers/layoutSlice";
import "./index.scss";
import { IframeRefreshOverlay } from "./IframeRefreshOverlay";
import { 
  useGetCourseworkQuery, 
  useGetOverallProgressQuery, 
  useGetTestScoresAndPerformanceQuery 
} from "services/homeApi";
import { 
  useGetPracticeTestsQuery,
  useGetTestResultsQuery 
} from "services/practice";
import { useLazyGetCourseIndexQuery } from "services/courseIndex";
import { useLazyGetAiRecommendationsQuery } from "services/aiRecommendations";
import { studyPlanner, useLazyGetStudyPlannerEventsQuery } from "services/studyPlanner";
import { homeApi } from "services/homeApi";
import { decodeToken } from "utils/functions/decodeToken";
import { isScoreReport } from "utils/functions/isScoreReport";

export const IframeModalManager = () => {
  const dispatch = useAppDispatch();
  const { iframeModal, aiMCATChatDrawerStatus } = useAppSelector((state) => state.layout);
  const { mode } = useAppSelector((state) => state.theme);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const lastPushedStateRef = useRef<string | null>(null);
  const wasOpenRef = useRef(false);
  const lastModalRef = useRef<{ url?: string | null; title?: string | null }>({
    url: null,
    title: null,
  });
  
  const payload = decodeToken();

  // Refetch hooks for automatic refresh
  const { refetch: courseWorkRefetch } = useGetCourseworkQuery({
    page: 1,
    selectedOption: 1,
    timeFilter: 1,
  }, { skip: !payload });
  
  const { refetch: overAllProgressRefetch } = useGetOverallProgressQuery({
    Userid: payload?.userid,
    Productcode: payload?.productcode,
    Offeringcode: payload?.offeringcode,
  }, { skip: !payload });
  
  const { refetch: testScoresRefetch } = useGetTestScoresAndPerformanceQuery(undefined, { skip: !payload });
  
  // Practice-specific queries for refreshing practice tab data
  const { refetch: practiceTestsRefetch } = useGetPracticeTestsQuery(undefined, { skip: !payload });
  const { refetch: testResultsRefetch } = useGetTestResultsQuery(undefined, { skip: !payload });
  
  const [triggerCourseIndexRefetch] = useLazyGetCourseIndexQuery();
  const [triggerRefetchAiRecommendation] = useLazyGetAiRecommendationsQuery();
  const [triggerStudyPlannerRefetch] = useLazyGetStudyPlannerEventsQuery();

  // Handle browser history management for modal
  useEffect(() => {
    if (!iframeModal.isOpen || !iframeModal.url) {
      return;
    }

    const statePayload = {
      modalOpen: true,
      iframeUrl: iframeModal.url,
      iframeTitle: iframeModal.title,
    };

    const payloadSignature = JSON.stringify(statePayload);

    if (lastPushedStateRef.current === payloadSignature) {
      return;
    }

    // Push a new history entry when modal opens with metadata
    window.history.pushState(statePayload, '', window.location.href);
    lastPushedStateRef.current = payloadSignature;
  }, [iframeModal.isOpen, iframeModal.url, iframeModal.title]);

  useEffect(() => {
    if (!iframeModal.isOpen) {
      lastPushedStateRef.current = null;
    }
  }, [iframeModal.isOpen]);

  // Handle browser back navigation
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const modalState = (event.state || {}) as {
        modalOpen?: boolean;
        iframeUrl?: string;
        iframeTitle?: string;
      };

      if (modalState.modalOpen) {
        event.preventDefault();

        if (iframeModal.isOpen) {
          dispatch(closeIframeModal());
          window.history.pushState(null, '', window.location.href);
        } else if (modalState.iframeUrl) {
          dispatch(openIframeModal({ url: modalState.iframeUrl, title: modalState.iframeTitle }));
        }

        return;
      }

      if (iframeModal.isOpen) {
        // Prevent default navigation and close the modal instead
        event.preventDefault();
        dispatch(closeIframeModal());
        // Stay on current page by pushing the current state back
        window.history.pushState(null, '', window.location.href);
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [iframeModal.isOpen, dispatch]);

  const handleAutoRefresh = useCallback(async (targetUrl?: string | null) => {
    // Only auto-refresh if content was actually accessed
    if (!targetUrl && !iframeModal.url) return;

    const MIN_REFRESH_DISPLAY_MS = 1500;

    console.log("Starting auto-refresh after iframe modal close");
    setIsRefreshing(true);
    dispatch(setIsIframeRefreshing(true));

    const minDisplayTime = new Promise((resolve) => setTimeout(resolve, MIN_REFRESH_DISPLAY_MS));

    const promises = [
      courseWorkRefetch(),
      triggerCourseIndexRefetch(),
      overAllProgressRefetch(), 
      testScoresRefetch(),
      practiceTestsRefetch(),
      testResultsRefetch(),
      triggerRefetchAiRecommendation(),
      triggerStudyPlannerRefetch(),
    ];

    try {
      const [results] = await Promise.all([
        Promise.allSettled(promises),
        minDisplayTime,
      ]);
      console.log("Auto-refresh results:", results);
      // Invalidate RTK Query cache to ensure fresh data
      dispatch(homeApi.util.invalidateTags([
        "Coursework",
        "Home",
        "CourseIndex",
        "Practice" // Add practice cache invalidation
      ]));
      dispatch(studyPlanner.util.invalidateTags([
        "StudyPlanner",
        "StudyPlannerPostForm",
        "StudyPlannerUpdateEvent"
      ]));
      console.log("Auto-refresh completed successfully");
    } catch (error) {
      console.error("Auto-refresh failed:", error);
    } finally {
      setIsRefreshing(false);
      dispatch(setIsIframeRefreshing(false));
    }
  }, [
    courseWorkRefetch,
    triggerCourseIndexRefetch,
    overAllProgressRefetch,
    testScoresRefetch,
    practiceTestsRefetch,
    testResultsRefetch,
    triggerRefetchAiRecommendation,
    triggerStudyPlannerRefetch,
    dispatch,
    iframeModal.url
  ]);

  const handleClose = () => {
    // Close modal immediately for better UX
    dispatch(closeIframeModal());

    // Clean up history state - replace the modal entry instead of leaving it in the stack
    // This prevents the modal from reopening when user presses browser back button
    const currentState = window.history.state;
    if (currentState && currentState.modalOpen) {
      window.history.replaceState(null, '', window.location.href);
    }
  };

  // Track last open modal details for post-close refresh decisions
  useEffect(() => {
    if (iframeModal.isOpen) {
      lastModalRef.current = {
        url: iframeModal.url,
        title: iframeModal.title,
      };
    }
  }, [iframeModal.isOpen, iframeModal.url, iframeModal.title]);

  // Centralized auto-refresh trigger when modal transitions from open -> closed
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    const isNowOpen = iframeModal.isOpen;

    if (wasOpen && !isNowOpen) {
      const { url, title } = lastModalRef.current;
      if (!isScoreReport(url, title)) {
        handleAutoRefresh(url).catch((error: unknown) => {
          console.error("Background auto-refresh failed:", error);
        });
      } else {
        console.log("Skipping auto-refresh for score report");
      }
    }

    wasOpenRef.current = isNowOpen;
  }, [iframeModal.isOpen]);

  return (
    <>
      <IframeModalView
        isOpen={iframeModal.isOpen}
        onClose={handleClose}
        url={iframeModal.url}
        theme={mode}
        title={iframeModal.title}
        aiChatDrawerStatus={aiMCATChatDrawerStatus}
      />

      <IframeRefreshOverlay
        isRefreshing={isRefreshing}
        aiChatDrawerStatus={aiMCATChatDrawerStatus}
        theme={mode}
      />
    </>
  );
};
